import * as React from 'react';
import { Box, Button, Link } from '@mui/material';
import { useEffect, useState, useContext } from 'react';
import {
  Base,
  Subheading
} from '@natomalabs/natomui/src/components/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  BuildingIcon,
  CheckIcon,
  ChevronDownIcon
} from '@natomalabs/natomui/src/icons';
import { usePathname } from 'next/navigation';
import { MeContext } from '../providers/Me';
import CircleLogo from './CircleLogo';
import { useGetMyOrgsQuery } from '../../__generated__/graphql';
import { orgUrl } from '../lib/paths';
import useEnvironment from '../providers/Environment';
import { testIds } from './SidebarLayout';

export default function OrgSwitcher() {
  const path = usePathname();
  const { natoma } = useEnvironment();
  const { currentOrg: currentOrgData } = useContext(MeContext);
  const { data } = useGetMyOrgsQuery();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentOrgName, setCurrentOrgName] = useState<string | undefined>(
    undefined
  );
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const hasMultipleOrgs = data?.myOrgs?.length && data.myOrgs.length > 1;

  useEffect(() => {
    if (currentOrgData) {
      setCurrentOrgName(currentOrgData.displayName!);
    }
  }, [currentOrgData]);

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      data-testid={testIds.sidebarOrgSwitcher}
    >
      <Button
        id="postioned-button"
        aria-controls={open ? 'positioned-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="tertiary"
        onClick={handleClick}
        fullWidth
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(1),
          px: theme.spacing(1.5),
          py: theme.spacing(1),
          borderRadius: theme.spacing(2),
          justifyContent: 'start',
          pointerEvents: hasMultipleOrgs ? 'auto' : 'none',
          cursor: hasMultipleOrgs ? 'pointer' : 'default',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: hasMultipleOrgs
              ? theme.palette.actionable.secondary.active.background
              : 'transparent'
          }
        })}
      >
        <CircleLogo width={24} height={24} />
        <Subheading
          sx={(theme) => ({
            textOverflow: 'ellipsis',
            color: theme.palette.text.primary,
            flex: 1,
            textAlign: 'start'
          })}
          noWrap
        >
          {currentOrgName ?? 'No Org Provided'}
        </Subheading>
        {hasMultipleOrgs ? <ChevronDownIcon /> : null}
      </Button>
      {hasMultipleOrgs ? (
        <Menu
          id="positioned-menu"
          aria-labelledby="positioned-button"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: (theme) => ({
                p: theme.spacing(1),
                width: anchorEl && anchorEl.offsetWidth
              })
            }
          }}
        >
          {data?.myOrgs.map((org, i) => (
            <MenuItem
              key={i}
              sx={(theme) => ({ gap: theme.spacing(1.25) })}
              onClick={handleClose}
              disabled={currentOrgName === org.displayName}
              component={Link}
              href={`${orgUrl(org.slug, natoma.rootDomain)}${path}`}
            >
              <BuildingIcon />
              <Base
                sx={{
                  flex: 1,
                  textOverflow: 'ellipsis'
                }}
                noWrap
              >
                {org.displayName}
              </Base>
              {currentOrgName === org.displayName && <CheckIcon />}
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </Box>
  );
}
